import { buildActualRaidUsage, buildRaidSimulationCandidates } from './raidFullSimulationPlan.js';
import { summarizeRaidStrategyProgress } from './raidStrategyProgress.js';
import {
  canonicalStrategyDeck,
  reconcileRaidStrategy,
  strategyMemberOpenid,
} from './raidStrategyReconciliation.js';

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function planFor(plans, bossNo, phaseNo) {
  return (Array.isArray(plans) ? plans : []).find((plan) => (
    numberValue(plan?.bossNo) === numberValue(bossNo) && numberValue(plan?.phaseNo) === numberValue(phaseNo)
  )) || null;
}

function actualsFor(actualRecords, bossNo, phaseNo) {
  return (Array.isArray(actualRecords) ? actualRecords : []).filter((record) => (
    numberValue(record?.bossNo ?? record?.boss_no) === numberValue(bossNo)
    && numberValue(record?.phaseNo ?? record?.phase_no) === numberValue(phaseNo)
  ));
}

// What the plan itself has already spent, in the shape findBossTetrisCombinations expects.
//
// Only planned attacks count here. Real attacks are already baked into every candidate by
// buildTetrisCandidates (`remainingAttacks` = 3 - actual hits, `actualUsedCodes` = Nikke the member
// really used), so counting them again would hide members who still have attacks left. Plans that a
// real attack already matched are reported as the actual attack by reconcileRaidStrategy, which is
// why this reads `effectiveAttacks` and keeps only `sourceType === 'plan'`.
//
// Without this the per-boss recommendation offers members who are already at 3 planned attacks or
// whose Nikke are planned on another boss; the board then drops those slots when it validates them,
// leaving the boss short of its target while reporting that the recommendation was added.
// `release` frees one boss×phase's planned attacks: a plan is a proposal, so when the question is
// "what else could this boss run", its own planned decks and the attacks they spend have to go back
// into the pool or the only answer is the plan that is already there.
export function strategyPlanUsage(effectiveAttacks = [], release = null) {
  const releaseBossNo = numberValue(release?.bossNo);
  const releasePhaseNo = numberValue(release?.phaseNo);
  const memberUsage = new Map();
  const memberUsedCodes = new Map();

  for (const attack of Array.isArray(effectiveAttacks) ? effectiveAttacks : []) {
    if (attack?.sourceType !== 'plan') continue;
    if (releaseBossNo > 0 && releasePhaseNo > 0
      && numberValue(attack?.bossNo) === releaseBossNo
      && numberValue(attack?.phaseNo) === releasePhaseNo) continue;
    const memberOpenid = strategyMemberOpenid(attack);
    if (!memberOpenid) continue;

    memberUsage.set(memberOpenid, (memberUsage.get(memberOpenid) || 0) + 1);
    const usedCodes = memberUsedCodes.get(memberOpenid) || new Set();
    for (const code of canonicalStrategyDeck(attack.deck)) usedCodes.add(code);
    memberUsedCodes.set(memberOpenid, usedCodes);
  }

  return { memberUsage, memberUsedCodes };
}

// What each boss×phase in `phases` still needs after real attacks and the plan itself, in the shape
// the full-raid simulator takes as a target. `remaining` is what is missing once the plan is counted,
// so bosses the plan already fills drop out of the simulator; `remainingAfterActualEok` is what is
// really missing, which is what to aim at when the plan itself is up for rewriting.
export function strategyPlanTargets({ bosses = [], plans = [], actualRecords = [], phases = [1, 2, 3] } = {}) {
  const targets = [];
  for (const phaseNo of phases) {
    for (const boss of Array.isArray(bosses) ? bosses : []) {
      const bossNo = numberValue(boss?.bossNo);
      if (bossNo < 1 || bossNo > 5) continue;
      const plan = planFor(plans, bossNo, phaseNo);
      const progress = summarizeRaidStrategyProgress({
        reconciliation: reconcileRaidStrategy({
          plans: plan ? [plan] : [],
          actualRecords: actualsFor(actualRecords, bossNo, phaseNo),
        }),
        targetHpEok: plan?.targetHpEok,
      });
      targets.push({
        id: `${phaseNo}-${bossNo}`,
        level: numberValue(phaseNo, 1),
        bossNo,
        bossName: String(boss?.bossName ?? '').trim() || `${bossNo}보스`,
        element: String(boss?.element ?? '').trim(),
        hp: progress.targetHpEok,
        remaining: progress.remainingAfterPlanEok,
        remainingAfterActualEok: progress.remainingAfterActualEok,
        order: (numberValue(phaseNo, 1) - 1) * 5 + bossNo - 1,
        actualEok: progress.actualDamageEok,
        plannedEok: progress.plannedDamageEok,
        projectedTotalEok: progress.projectedTotalEok,
        targetReached: progress.targetReached,
      });
    }
  }
  return targets;
}

// Feeds the plan into the full-raid simulator instead of filling boss by boss in a fixed order:
// targets are what the plan still lacks, and everything the plan and the real raid already spent is
// seeded as prior usage, so the simulator plans the remaining attacks across all bosses at once
// under the same 3-attack and Nikke-reuse limits.
export function buildStrategySimulationInput({
  bosses = [],
  plans = [],
  actualRecords = [],
  candidates = [],
  phases = [1, 2, 3],
  fitMode = 'kill',
  allowedErrorEok = 0,
  memberCount = 0,
} = {}) {
  const targets = strategyPlanTargets({ bosses, plans, actualRecords, phases })
    .filter((target) => target.remaining > 0);

  const reconciled = reconcileRaidStrategy({ plans, actualRecords });
  const planUsage = strategyPlanUsage(reconciled.effectiveAttacks);
  const { initialUsage, initialDeckKeys } = buildActualRaidUsage(actualRecords);
  for (const [memberOpenid, count] of planUsage.memberUsage.entries()) {
    initialUsage.set(memberOpenid, Math.min(3, (initialUsage.get(memberOpenid) || 0) + count));
  }
  for (const [memberOpenid, codes] of planUsage.memberUsedCodes.entries()) {
    const used = initialDeckKeys.get(memberOpenid) || new Set();
    codes.forEach((code) => used.add(code));
    initialDeckKeys.set(memberOpenid, used);
  }

  const usedPracticeIds = new Set((reconciled.effectiveAttacks || [])
    .filter((attack) => attack?.sourceType === 'plan')
    .map((attack) => String(attack?.practiceId ?? '').trim())
    .filter(Boolean));

  return {
    targets,
    candidates: buildRaidSimulationCandidates(
      (Array.isArray(candidates) ? candidates : []).filter((candidate) => !usedPracticeIds.has(candidate?.id)),
    ),
    initialUsage,
    initialDeckKeys,
    initialUsedSlotIds: [...usedPracticeIds],
    memberCount: Math.max(numberValue(memberCount), initialUsage.size),
    beamWidth: 36,
    partialWidth: 180,
    planLimit: 3,
    combosPerTarget: 4,
    maxPerMemberBoss: 5,
    maxCandidatesPerTarget: 56,
    fitMode: fitMode === 'nearest' ? 'nearest' : 'kill',
    allowedErrorEok: Math.max(0, numberValue(allowedErrorEok)),
  };
}
